import {
  afterNextRender,
  Component,
  computed,
  ElementRef,
  forwardRef,
  HostBinding,
  inject,
  input,
  linkedSignal,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AbstractControl } from '../abstract-control/abstract-control';
import { Icon } from '../../icon/icon';
import { Spinner } from '../../spinner/spinner';
import { CSSValuePipe } from '@pipes/css-value-pipe';
import { AsSignalPipe } from '@pipes/as-signal-pipe';
import { Translate } from '@services/core/translate';
import type { SelectItem } from '@appTypes';

@Component({
  selector: 'app-select',
  standalone: true,
  imports: [MatTooltipModule, CSSValuePipe, AsSignalPipe, Icon, Spinner],
  templateUrl: './select.html',
  styleUrls: ['./../abstract-control/abstract-control.scss', './select.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Select),
      multi: true,
    },
  ],
})
export class Select extends AbstractControl<string | number | undefined, SelectItem> {
  // Dependencias
  public readonly t = inject(Translate).translateFn();

  // View refs
  $inputElement = viewChild.required<ElementRef<HTMLInputElement>>('input');

  // Inputs
  $items = model.required<SelectItem[]>({ alias: 'items' });
  override $value = input<string | number | undefined>(undefined, { alias: 'value' });
  $_value = linkedSignal(this.$value);
  $loading = input<boolean>(false, { alias: 'loading' });
  $_placeholder = input<string | null | undefined>(undefined, { alias: 'placeholder' });
  private $placeholder = linkedSignal(this.$_placeholder);
  $inputWidth = input<string>('auto', { alias: 'inputWidth' });
  $hostWidth = input<string>('fit-content', { alias: 'hostWidth' });
  $hidePlaceholder = input<boolean>(false, { alias: 'hidePlaceholder' });
  $disablePlaceholder = input<boolean>(false, { alias: 'disablePlaceholder' });

  // Outputs
  override valueChange = output<SelectItem>();
  keyString = output<string>();
  valueString = output<string>();
  keyNumber = output<number>();
  valueNumber = output<number>();

  // Signals / atributos
  override $controlValue = signal<string | number>('');
  $selectedValue = signal<string | number>('');

  // Bindings
  @HostBinding('style.width')
  get hostWidth(): string { return this.$hostWidth(); }

  // Signals computados

  // Placeholder a mostrar cuando no haya una selección predefinida
  $displayPlaceholder = computed<string | null>(() => {
    const inputValue = (this.$value() !== undefined ? this.$value() : this.$controlValue()) as string | number;
    if (this.$hidePlaceholder() && inputValue !== null && inputValue !== '')
      return null;

    const inputPlaceholder = this.$placeholder();
    if (inputPlaceholder === undefined)
      return `-- ${ this.t('select')() } --`;

    if (inputPlaceholder === null)
      return null;

    return inputPlaceholder;
  });

  // Constructor
  constructor() {
    super();

    // Verificar si se emitirá el valor al iniciar
    afterNextRender(() => {
      if (this.$emitAtStart()) {
        const inputValue = (this.$value() !== undefined
          ? this.$value()
          : this.$controlValue()) as string | number;

        const item = this.findItem(inputValue);

        if (item) {
          this.valueChange.emit(item);
          this.keyString.emit(String(item.key));
          this.valueString.emit(String(item.value));
          if (typeof item.key === 'number')
            this.keyNumber.emit(Number(item.key));
          if (typeof item.key === 'number')
            this.valueNumber.emit(Number(item.value));
        }
      }
    });

  };

  // Maneja el evento de cambio de valor desde el input
  override onValueChange(event: Event): void {
    if (this.$hidePlaceholder() && this.$placeholder() !== null)
      this.$placeholder.set(null);

    const newValue = (event.target as HTMLSelectElement).value;
    this.$_value.set(newValue);
    this.updateValue(newValue);

    const item = this.findItem(newValue);

    if (item) {
      this.valueChange.emit(item);
      this.keyString.emit(String(item.key));
      this.valueString.emit(String(item.value));
      if (typeof item.key === 'number')
        this.keyNumber.emit(Number(item.key));
      if (typeof item.key === 'number')
        this.valueNumber.emit(Number(item.value));
    }
    else {
      this.valueChange.emit({ key: '', value: '' });
      this.keyString.emit(String(''));
      this.valueString.emit(String(''));
      this.keyNumber.emit(-100);
      this.valueNumber.emit(-100);
    }
  }

  // Busca y devuelve el item correspondiente a la llave indicada
  private findItem(key: string | number | null): SelectItem | null {
    if (!key)
      return null;
    return this.$items().find(item => item.key == key) ?? null;
  }
}
